import {
  BaseSyntheticEvent,
  ComponentProps,
  memo,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useDispatch } from 'react-redux';

import { useTranslation } from '@mint-lib/i18n';
import styled, { useTheme } from '@mint-lib/styled';
import type {} from '@mint-lib/theme/src/extensions/palette.js';
import { format } from 'date-fns';

import { Box, Card, Divider, Icon, Typography } from '@myn/mui';

import { PLATFORM_NAMES } from '../../../constants/integrations.js';
import {
  useIntegrationAccountTypeRouteApi,
  useIntegrationPaths,
} from '../../../hooks/useIntegrationsRouteApi.js';
import { getAdformAdvertisers } from '../../../redux/action/api/IntegrationsApiActions.js';

type Account = {
  id: string;
  name: string;
  platform: string;
  logo: string | null;
  isRefreshTokenValid?: boolean;
  isOauthConnected?: boolean;
  expired?: boolean;
  isActive?: boolean;
  isDisabled?: boolean;
  advertiser?: string | null;
  connectedAt?: string;
  userUpdatedAt?: string;
  connectedBy?: string;
  integrationName: string;
  integration: string;
};
type AccountItemType = {
  item: Account;
};

const AccountItem = ({ item }: AccountItemType) => {
  const { t } = useTranslation('@myn/integrations_v2');

  const theme = useTheme();
  const dispatch = useDispatch();
  const accountTypeRoute = useIntegrationAccountTypeRouteApi();
  const navigate = accountTypeRoute.useNavigate();
  const paths = useIntegrationPaths();
  const search = accountTypeRoute.useSearch();
  const queryParamId = search.id;
  const paramsState = search.state;
  const { integration, tabName, type } = accountTypeRoute.useParams();

  const showWarningIcon =
    item.isRefreshTokenValid === false ||
    item.expired ||
    item.isActive === false ||
    item.isDisabled === true ||
    (integration === PLATFORM_NAMES.ADFORM && item.advertiser === null);

  const handleClick = useCallback(
    (e: BaseSyntheticEvent) => {
      if (integration === PLATFORM_NAMES.ADFORM) {
        dispatch<any>(getAdformAdvertisers(item.id));
      }

      navigate({
        to: paths.integrationType,
        params: {
          integration,
          tabName: 'accounts',
          type: 'update',
        },
        search: {
          id: item.id,
        },
      });
    },
    [item],
  );

  let accountId;

  if (paramsState) {
    const [, , , stateAccountId] = paramsState.split('.');
    accountId = stateAccountId;
  }
  const selected =
    (tabName === 'accounts' &&
      type === 'update' &&
      queryParamId === `${item.id}`) ||
    `${item.id}` === accountId;

  const element = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (selected) {
      setTimeout(() => {
        element.current?.scrollIntoView({ behavior: 'smooth' });
      }, 0);
      if (integration === PLATFORM_NAMES.ADFORM) {
        dispatch<any>(getAdformAdvertisers(item.id));
      }
    }
  }, [selected]);

  const userUpdateTime = (updateDate?: string | null) => {
    return updateDate ? (
      <>
        <Divider flexItem orientation="vertical" sx={{ margin: '0 3px' }} />
        <Typography variant="caption02" color="secondary">
          {format(new Date(updateDate), 'P')} {t('at')}{' '}
          {format(new Date(updateDate), 'p').replace(' ', '').toLowerCase()}
        </Typography>
      </>
    ) : null;
  };

  return (
    <StyledCard
      data-testid="account-item-test-id"
      ref={element}
      selected={!!selected}
      onClick={handleClick}
    >
      <Logo data-testid="account-item-platform-logo-block">
        {item.logo ? (
          <img src={item.logo} />
        ) : (
          <Icon component="Settings" fontSize="large" />
        )}
      </Logo>
      <div className="w-full max-w-[calc(100%-40px-8px)]">
        <div className="flex w-full flex-row items-center justify-between">
          <Typography
            variant="h5"
            mr={3}
            textOverflow="ellipsis"
            overflow="hidden"
          >
            {item.name}
          </Typography>
          {showWarningIcon ? (
            <Icon
              component="WarningAltFilled"
              htmlColor={theme.palette.warning.main}
              data-testid="account-item-warning-sign"
            />
          ) : null}
        </div>
        {item.connectedBy && (item.connectedAt || item.userUpdatedAt) && (
          <div className="mt-[5px] flex w-full">
            <Typography
              variant="caption02"
              color="secondary"
              noWrap
              display="block"
              maxWidth="calc(100% - 95px)" // 95px - average width of time and divider
            >
              {t('Last updated by:')} {item.connectedBy}
            </Typography>
            {userUpdateTime(item.connectedAt ?? item.userUpdatedAt)}
          </div>
        )}
      </div>
    </StyledCard>
  );
};

type CardProps = ComponentProps<typeof Card> & { selected: boolean };

// TODO: migrate to tailwind - styled with options argument (e.g., shouldForwardProp)
const StyledCard = styled(
  ({ sx = { mx: 1, ml: 0, mr: 2, p: 1.5, gap: 1 }, ...props }: CardProps) => (
    <Card sx={sx} {...props} />
  ),
)`
  height: 64px;
  display: flex;
  align-items: center;
  cursor: pointer;
  &:focus {
    background-color: ${({ theme }) => theme.palette.background.tertiary};
    border: 1px solid
      ${({ selected, theme }) =>
        selected
          ? theme.palette.tertiary.main
          : theme.palette.background.tertiary};
  }
`;

// TODO: migrate to tailwind - Contains nested selector or at-rule: (?:^|[;\s}])(?:[a-z][a-z0-9]*)\s*\{
const Logo = styled(Box)`
  img {
    height: 40px;
    width: 40px;
  }
  svg {
    height: 40px;
    width: 40px;
  }
`;
export default memo(AccountItem);
